import React, { useState, useEffect, useMemo } from "react";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { RevenueCharts } from "./DashboardEcommerceCharts";
import CountUp from "react-countup";
import { useSelector, useDispatch } from "react-redux";
import { getRevenueChartsData } from "../../slices/thunks";
import { createSelector } from "reselect";
import { getDashboardStats } from "../../api/ecommerce";

const emptyChartSeries = () => [
  { name: "Orders", type: "area", data: Array(12).fill(0) },
  { name: "Earnings", type: "bar", data: Array(12).fill(0) },
  { name: "Refunds", type: "line", data: Array(12).fill(0) },
];

const RevenueStats = () => {
  const [stats, setStats] = useState({
    ordersCount: 0,
    revenue: 0,
    refunds: 0,
    conversionRatio: 0,
  });
  useEffect(() => {
    getDashboardStats().then((r) => {
      const d = r?.data || r || {};
      setStats({
        ordersCount: d.ordersCount || 0,
        revenue: d.revenue || 0,
        refunds: d.refunds || 0,
        conversionRatio: d.conversionRatio ?? 0,
      });
    });
  }, []);
  return (
    <Row className="g-0 text-center">
      <Col xs={6} sm={3}>
        <div className="p-3 border border-dashed border-start-0">
          <h5 className="mb-1">
            <CountUp start={0} end={stats.ordersCount} duration={2} separator="," />
          </h5>
          <p className="text-muted mb-0">Orders</p>
        </div>
      </Col>
      <Col xs={6} sm={3}>
        <div className="p-3 border border-dashed border-start-0">
          <h5 className="mb-1">
            <CountUp prefix="£" start={0} end={stats.revenue} decimals={2} duration={2} />
          </h5>
          <p className="text-muted mb-0">Earnings</p>
        </div>
      </Col>
      <Col xs={6} sm={3}>
        <div className="p-3 border border-dashed border-start-0">
          <h5 className="mb-1">
            <CountUp prefix="£" start={0} end={stats.refunds} decimals={2} duration={2} />
          </h5>
          <p className="text-muted mb-0">Refunds</p>
        </div>
      </Col>
      <Col xs={6} sm={3}>
        <div className="p-3 border border-dashed border-start-0 border-end-0">
          <h5 className="mb-1 text-success">
            <CountUp start={0} end={stats.conversionRatio} decimals={2} duration={2} suffix="%" />
          </h5>
          <p className="text-muted mb-0">Orders / Products</p>
        </div>
      </Col>
    </Row>
  );
};

const selectRevenueChart = createSelector(
  (state) => state.DashboardEcommerce,
  (dashboard) => dashboard.revenueData
);

const Revenue = () => {
  const dispatch = useDispatch();
  const revenuePayload = useSelector(selectRevenueChart);

  const chartSeries = useMemo(() => {
    const s = revenuePayload?.series;
    if (s && Array.isArray(s) && s.length === 3 && s[0]?.data?.length) {
      return JSON.parse(JSON.stringify(s));
    }
    return emptyChartSeries();
  }, [revenuePayload]);

  const chartCategories = revenuePayload?.categories;

  const onChangeChartPeriod = (pType) => {
    dispatch(getRevenueChartsData(pType));
  };

  useEffect(() => {
    dispatch(getRevenueChartsData("all"));
  }, [dispatch]);

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Revenue</h4>
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn btn-soft-secondary btn-sm"
              onClick={() => {
                onChangeChartPeriod("all");
              }}
            >
              ALL
            </button>
            <button
              type="button"
              className="btn btn-soft-secondary btn-sm"
              onClick={() => {
                onChangeChartPeriod("month");
              }}
            >
              1M
            </button>
            <button
              type="button"
              className="btn btn-soft-secondary btn-sm"
              onClick={() => {
                onChangeChartPeriod("halfyear");
              }}
            >
              6M
            </button>
            <button
              type="button"
              className="btn btn-soft-primary btn-sm"
              onClick={() => {
                onChangeChartPeriod("year");
              }}
            >
              1Y
            </button>
          </div>
        </CardHeader>

        <CardHeader className="p-0 border-0 bg-light-subtle">
          <RevenueStats />
        </CardHeader>

        <CardBody className="p-0 pb-2">
          <div className="w-100">
            <div dir="ltr">
              <RevenueCharts
                series={chartSeries}
                categories={chartCategories}
                dataColors='["--vz-primary", "--vz-success", "--vz-danger"]'
              />
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default Revenue;
